// AIDEV-NOTE: THIS IMPORT MUST STAY FIRST. It captures and strips the magic-link
// query params (the bearer token on /auth/link, the email + from=expired on the
// /auth door) as a module side effect. Import order is load-bearing, not
// cosmetic: `./router` below builds its router at module scope and snapshots
// `window.location` while doing so, and ES imports are evaluated before any
// statement in this file's body. Running the capture as a statement instead --
// which is what this file used to do -- left the token in the router's snapshot
// for the life of the document, because `history.replaceState` fires no
// popstate. Anything reading `useLocation()` (AnalyticsBootstrapper's
// `landing_path`, AuthDoor's mount effect) would still see it.
// See src/lib/magicLink/bootstrapAuthLinkParams.ts and authLinkParams.ts.
import './lib/magicLink/bootstrapAuthLinkParams';
// AIDEV-NOTE: captured+stripped before ./router; see bootstrapAuthLinkParams.ts
import './utils/bootstrapChunkReloadParam';
// AIDEV-NOTE: before ./router too: returns a big screen signed out from to its screen link (TECH-867 L19)
import './lib/livePolls/bootstrapScreenAfterSignOut';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from './components/ui/sonner';
import { GlobalShareSheet } from './components/GlobalShareSheet';
import { GlobalOnboarding } from './components/GlobalOnboarding';
import { CelebrationHost } from './components/celebration/CelebrationHost';
import { GlobalActivationDrawers } from './components/activation/GlobalActivationDrawers';
import { NativeBackButton } from './components/NativeBackButton';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { initializeAmplitude } from './lib/amplitude';
import { initWebVitals } from './lib/webVitals';
import { initOneSignal } from './lib/onesignal';
import { registerServiceWorkerPrompts } from './lib/pwa/register-service-worker-prompts';
import { installChunkErrorHandlers } from './utils/chunkLoadErrorHandler';
import { initDevTitle } from './utils/devTitle';
import { captureAttribution } from './lib/attribution';
import { logger } from './utils/logger';
import { initNativePlugins } from './lib/capacitor-init';
import { MaintenanceTakeover } from './components/maintenance/MaintenanceTakeover';
import {
  initializeMaintenance,
  subscribe as subscribeMaintenance,
  getState as getMaintenanceState,
} from './lib/maintenance';
import { registerRecoveryInvalidation } from './lib/maintenance/recoveryInvalidation';
import { createAppQueryClient } from './lib/appQueryClient';
import { removeLegacySignedInCueMirror } from './lib/magicLink/signedInCue';

declare global {
  interface Window {
    __react_root__?: ReturnType<typeof createRoot>;
  }
}

// Install chunk error handlers early to catch any dynamic import failures
installChunkErrorHandlers();

// In dev environment, prefix document title with [dev]
initDevTitle();

registerServiceWorkerPrompts();

// AIDEV-NOTE: Capture attribution parameters (UTM, click IDs) on page load for signup tracking
// Must happen early before any navigation that might lose URL parameters
captureAttribution();

// The pre-#1117 profile-wide copy of the "Signed in as" cue; nothing reads it now.
removeLegacySignedInCueMirror();

// AIDEV-NOTE: Initialize Capacitor native plugins (status bar, safe area).
// Fire-and-forget: no-ops on web, sets up system bar styling on native.
initNativePlugins().catch((e: unknown) => {
  logger.error('Native plugin init failed', e as Error);
});

// AIDEV-NOTE: Initialize Web Vitals tracking early for React Compiler migration baseline
// Collects CLS, FCP, INP, LCP, TTFB metrics and sends to Amplitude
initWebVitals();

// Fire-and-forget OneSignal web SDK bootstrap. Dormant (immediate no-op that
// never rejects) unless VITE_ONESIGNAL_APP_ID is set — so this is inert in every
// environment today. See src/lib/onesignal.ts for the scoped-worker rationale.
void initOneSignal();

// Maintenance system bootstrap: subscribes the recovery-poller wiring,
// applies the dev gate (VITE_DEV_MAINTENANCE_INITIAL / VITE_DEV_MAINTENANCE_MESSAGE),
// and attaches the visibilitychange + online listeners. See
// src/lib/maintenance/index.ts for the full sequencing rationale.
initializeMaintenance();

// Lazy-initialize Amplitude after first paint / when browser is idle to reduce initial mount side-effects
if (typeof window !== 'undefined') {
  const startAmplitudeInit = () => {
    try {
      initializeAmplitude();
    } catch (e) {
      logger.error('Deferred Amplitude init failed', e as Error);
    }
  };
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(startAmplitudeInit, { timeout: 2000 });
    } else {
      setTimeout(startAmplitudeInit, 0);
    }
  };
  // Prefer post-paint
  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
}
// Persistent storage is best-effort and capability-detected. Two-level
// guard: `navigator.storage` itself is `undefined` on iOS Safari in
// non-secure contexts (e.g., http://192.168.x.y from a phone on the LAN
// during local dev), and `.persist` is missing on older Safari versions.
// Without BOTH guards, a TypeError at module init blocks React from
// mounting entirely — we'd ship a blank page to anyone hitting a
// non-secure origin.
const storage = navigator.storage as StorageManager | undefined;
if (storage && typeof storage.persist === 'function') {
  void storage
    .persist()
    .then(granted => {
      logger.info(`Storage persistence granted: ${String(granted)}`);
    })
    .catch((error: unknown) => {
      logger.warn('Storage persistence request failed', error);
    });
}

// The app's TanStack Query client; its defaults live in @lib/appQueryClient, which the dev
// harnesses build their clients with too.
export const queryClient = createAppQueryClient();

// When maintenance recovers, invalidate all queries so cached failure states
// refetch fresh data. The wiring is extracted into registerRecoveryInvalidation
// so the recovery comparator can be unit-tested without booting React.
const unsubscribeMaintenance = registerRecoveryInvalidation(
  queryClient,
  subscribeMaintenance,
  getMaintenanceState,
);

// Dev-only: prevent listener accumulation across Vite HMR re-evals. Mirrors
// the pattern used by the maintenance module's own listeners in
// src/lib/maintenance/index.ts.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unsubscribeMaintenance();
  });
}

// AIDEV-NOTE: HMR-safe root creation - prevents "Node.removeChild" errors during dev
// We intentionally use __ prefix to mark this as internal/private state owned by our app.
// Without this, Vite HMR creates multiple React roots -> "Target container is not a DOM element" errors.
// This is our own private marker, not a React internal, so using __ is acceptable here.
const container = document.getElementById('app');
if (!container) {
  throw new Error('Missing #app container');
}
let root = window.__react_root__;

if (!root) {
  root = createRoot(container);
  window.__react_root__ = root;
}

root.render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools />
        <RouterProvider router={router} />
        <MaintenanceTakeover router={router} />
        <Toaster />
        <GlobalShareSheet />
        <GlobalOnboarding />
        <CelebrationHost />
        <NativeBackButton />
        <GlobalActivationDrawers />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
